import React, {
    useEffect,
    useState
} from "react";

import Swal from "sweetalert2";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import UserSidebar from "../components/user/UserSidebar";
import UserHeader from "../components/user/UserHeader";

import {
    getFreeMCQQuestions,
    submitFreeMCQ
} from "../config/userApi";

import "./FreeMCQExam.css";


function FreeMCQExam() {

    const navigate = useNavigate();

    const { paperId } = useParams();


    /*
    |--------------------------------------------------------------------------
    | Default Exam Duration
    |--------------------------------------------------------------------------
    */

    const DEFAULT_EXAM_DURATION = 30 * 60;


    /*
    |--------------------------------------------------------------------------
    | Sidebar
    |--------------------------------------------------------------------------
    */

    const [
        sidebarOpen,
        setSidebarOpen
    ] = useState(false);


    const [name] =
        useState("Student");


    /*
    |--------------------------------------------------------------------------
    | Mobile Question Navigator
    |--------------------------------------------------------------------------
    */

    const [
        navigatorOpen,
        setNavigatorOpen
    ] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Questions
    |--------------------------------------------------------------------------
    */

    const [
        questions,
        setQuestions
    ] = useState([]);


    /*
    |--------------------------------------------------------------------------
    | Paper
    |--------------------------------------------------------------------------
    */

    const [
        paper,
        setPaper
    ] = useState(null);


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    const [
        loading,
        setLoading
    ] = useState(true);


    /*
    |--------------------------------------------------------------------------
    | Error
    |--------------------------------------------------------------------------
    */

    const [
        error,
        setError
    ] = useState("");


    /*
    |--------------------------------------------------------------------------
    | Remaining Time
    |--------------------------------------------------------------------------
    */

    const [
        remainingTime,
        setRemainingTime
    ] = useState(
        DEFAULT_EXAM_DURATION
    );


    /*
    |--------------------------------------------------------------------------
    | Exam Start Time
    |--------------------------------------------------------------------------
    */

    const [
        examStartTime,
        setExamStartTime
    ] = useState("");


    /*
    |--------------------------------------------------------------------------
    | Current Question
    |--------------------------------------------------------------------------
    */

    const [
        currentQuestion,
        setCurrentQuestion
    ] = useState(0);


    /*
    |--------------------------------------------------------------------------
    | Answers
    |--------------------------------------------------------------------------
    */

    const [
        answers,
        setAnswers
    ] = useState({});


    /*
    |--------------------------------------------------------------------------
    | Tagged Questions
    |--------------------------------------------------------------------------
    */

    const [
        taggedQuestions,
        setTaggedQuestions
    ] = useState([]);


    /*
    |--------------------------------------------------------------------------
    | Submitting
    |--------------------------------------------------------------------------
    */

    const [
        submitting,
        setSubmitting
    ] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Fetch Questions
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        let mounted = true;


        const fetchQuestions = async () => {

            try {

                setLoading(true);

                setError("");


                if (!paperId) {

                    setError(
                        "Paper ID is missing."
                    );

                    return;

                }


                const response =
                    await getFreeMCQQuestions(
                        paperId
                    );


                const data =
                    await response.json();


                if (!response.ok) {

                    throw new Error(
                        data.message ||
                        "Unable to load questions."
                    );

                }


                if (!data.status) {

                    throw new Error(
                        data.message ||
                        "Unable to load questions."
                    );

                }


                if (!mounted) {

                    return;

                }


                /*
                |--------------------------------------------------------------------------
                | Paper
                |--------------------------------------------------------------------------
                */

                setPaper(
                    data.paper || null
                );


                /*
                |--------------------------------------------------------------------------
                | Questions
                |--------------------------------------------------------------------------
                */

                const apiQuestions =
                    Array.isArray(data.data)
                        ? data.data
                        : [];


                const formattedQuestions =
                    apiQuestions.map(
                        (item, index) => ({

                            id:
                                item.question_id,

                            question:
                                item.question || "",

                            option1:
                                item.option1 || "",

                            option2:
                                item.option2 || "",

                            option3:
                                item.option3 || "",

                            option4:
                                item.option4 || "",

                            explanation:
                                item.explanation || "",

                            Q_number:
                                item.Q_number ||
                                index + 1,

                            sort_order:
                                item.sort_order ||
                                index + 1

                        })
                    );


                setQuestions(
                    formattedQuestions
                );


                /*
                |--------------------------------------------------------------------------
                | Attempt Number
                |--------------------------------------------------------------------------
                */

                console.log(
                    "Current Attempt:",
                    data.attempt_number
                );


                /*
                |--------------------------------------------------------------------------
                | Duration
                |--------------------------------------------------------------------------
                */

                const duration =
                    Number(
                        data.paper?.duration ||
                        1
                    );


                setRemainingTime(
                    duration * 60
                );


                /*
                |--------------------------------------------------------------------------
                | Exam Start Time
                |--------------------------------------------------------------------------
                */

                setExamStartTime(
                    new Date().toISOString()
                );


            } catch (error) {

                console.error(
                    "Free MCQ Questions Error:",
                    error
                );


                if (mounted) {

                    setError(
                        error.message ||
                        "Unable to load questions."
                    );

                }

            } finally {

                if (mounted) {

                    setLoading(false);

                }

            }

        };


        fetchQuestions();


        return () => {

            mounted = false;

        };


    }, [paperId]);


    /*
    |--------------------------------------------------------------------------
    | Countdown Timer
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (
            loading ||
            questions.length === 0 ||
            submitting
        ) {

            return;

        }


        if (
            remainingTime <= 0
        ) {

            handleSubmit(true);

            return;

        }


        const timer =
            setInterval(() => {

                setRemainingTime(
                    previous =>
                        previous - 1
                );

            }, 1000);


        return () => {

            clearInterval(timer);

        };

    }, [
        remainingTime,
        loading,
        questions.length,
        submitting
    ]);


    /*
    |--------------------------------------------------------------------------
    | Format Remaining Time
    |--------------------------------------------------------------------------
    */

    const minutes =
        Math.floor(
            remainingTime / 60
        );


    const seconds =
        remainingTime % 60;


    const formattedTime =
        `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;


    /*
    |--------------------------------------------------------------------------
    | Current Question
    |--------------------------------------------------------------------------
    */

    const question =
        questions[currentQuestion];


    /*
    |--------------------------------------------------------------------------
    | Counts
    |--------------------------------------------------------------------------
    */

    const attemptedCount =
        Object.keys(
            answers
        ).length;


    const taggedCount =
        taggedQuestions.length;


    const unattemptedCount =
        Math.max(
            questions.length -
            attemptedCount,
            0
        );


    /*
    |--------------------------------------------------------------------------
    | Select Answer
    |--------------------------------------------------------------------------
    */

    const handleAnswer = (
        answer
    ) => {

        if (!question) {

            return;

        }


        setAnswers(
            previous => ({

                ...previous,

                [question.id]:
                    answer

            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */

    const handleNext = () => {

        if (
            currentQuestion <
            questions.length - 1
        ) {

            setCurrentQuestion(
                previous =>
                    previous + 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */

    const handlePrevious = () => {

        if (
            currentQuestion > 0
        ) {

            setCurrentQuestion(
                previous =>
                    previous - 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Go To Question
    |--------------------------------------------------------------------------
    */

    const goToQuestion = (
        index
    ) => {

        if (
            index >= 0 &&
            index < questions.length
        ) {

            setCurrentQuestion(
                index
            );

            setNavigatorOpen(
                false
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Tag / Untag
    |--------------------------------------------------------------------------
    */

    const toggleTag = () => {

        if (!question) {

            return;

        }


        setTaggedQuestions(
            previous => {

                if (
                    previous.includes(
                        question.id
                    )
                ) {

                    return previous.filter(
                        id =>
                            id !==
                            question.id
                    );

                }


                return [
                    ...previous,
                    question.id
                ];

            }
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (
        automatic = false
    ) => {

        if (
            submitting
        ) {

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Confirmation
        |--------------------------------------------------------------------------
        */

        if (!automatic) {

            const confirmSubmit =
                await Swal.fire({

                    title: "Submit Paper?",

                    html: `
                        <div style="text-align:left;">

                            <p style="margin-bottom:15px;">
                                Are you sure you want to submit this paper?
                            </p>

                            <div style="
                                display:grid;
                                grid-template-columns:repeat(3, 1fr);
                                gap:10px;
                                margin-top:15px;
                            ">

                                <div style="
                                    padding:10px;
                                    background:#f0fdf4;
                                    border-radius:8px;
                                    text-align:center;
                                ">

                                    <strong style="
                                        display:block;
                                        font-size:20px;
                                    ">
                                        ${attemptedCount}
                                    </strong>

                                    <small>
                                        Attempted
                                    </small>

                                </div>


                                <div style="
                                    padding:10px;
                                    background:#fff7ed;
                                    border-radius:8px;
                                    text-align:center;
                                ">

                                    <strong style="
                                        display:block;
                                        font-size:20px;
                                    ">
                                        ${unattemptedCount}
                                    </strong>

                                    <small>
                                        Unattempted
                                    </small>

                                </div>


                                <div style="
                                    padding:10px;
                                    background:#eff6ff;
                                    border-radius:8px;
                                    text-align:center;
                                ">

                                    <strong style="
                                        display:block;
                                        font-size:20px;
                                    ">
                                        ${taggedCount}
                                    </strong>

                                    <small>
                                        Tagged
                                    </small>

                                </div>

                            </div>

                        </div>
                    `,

                    icon: "question",

                    showCancelButton: true,

                    confirmButtonText:
                        "Submit Paper",

                    cancelButtonText:
                        "Continue",

                    reverseButtons: true,

                    allowOutsideClick: false,

                    allowEscapeKey: true

                });


            /*
            |--------------------------------------------------------------------------
            | Continue Exam
            |--------------------------------------------------------------------------
            */

            if (
                !confirmSubmit.isConfirmed
            ) {

                return;

            }

        }


        try {

            setSubmitting(true);

            setNavigatorOpen(false);


            /*
            |--------------------------------------------------------------------------
            | Prepare Answers
            |--------------------------------------------------------------------------
            */

            const submissionAnswers = {};


            questions.forEach(
                (item) => {

                    submissionAnswers[
                        item.id
                    ] =
                        answers[item.id] ||
                        "0";

                }
            );


            /*
            |--------------------------------------------------------------------------
            | Submit To Laravel
            |--------------------------------------------------------------------------
            */

            const response =
                await submitFreeMCQ(

                    paperId,

                    submissionAnswers,

                    examStartTime

                );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(
                    data.message ||
                    "Unable to submit paper."
                );

            }


            if (!data.status) {

                throw new Error(
                    data.message ||
                    "Unable to submit paper."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Go To Result Page
            |--------------------------------------------------------------------------
            */

            navigate(
    `/show-free-result/${paperId}`,
    {
        replace: true,
        state: {
            result: data.data,
            paper: data.data?.paper || paper
        }
    }
);


        } catch (error) {

            console.error(
                "Submit Free MCQ Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Submission Failed",

                text:
                    error.message ||
                    "Unable to submit paper.",

                confirmButtonText:
                    "OK"

            });


        } finally {

            setSubmitting(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="user-dashboard">

                <UserSidebar
                    sidebarOpen={
                        sidebarOpen
                    }
                    setSidebarOpen={
                        setSidebarOpen
                    }
                />


                <main className="dashboard-main">

                    <UserHeader
                        name={name}
                        setSidebarOpen={
                            setSidebarOpen
                        }
                    />


                    <div className="free-exam-page">

                        <div className="result-card">

                            <div className="result-icon">

                                <i className="bi bi-hourglass-split"></i>

                            </div>


                            <h1>
                                Loading Paper...
                            </h1>


                            <p className="result-subtitle">

                                Please wait while the questions are loading.

                            </p>

                        </div>

                    </div>

                </main>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Error
    |--------------------------------------------------------------------------
    */

    if (error) {

        return (

            <div className="user-dashboard">

                <UserSidebar
                    sidebarOpen={
                        sidebarOpen
                    }
                    setSidebarOpen={
                        setSidebarOpen
                    }
                />


                <main className="dashboard-main">

                    <UserHeader
                        name={name}
                        setSidebarOpen={
                            setSidebarOpen
                        }
                    />


                    <div className="free-exam-page">

                        <div className="result-card">

                            <div className="result-icon">

                                <i className="bi bi-exclamation-circle-fill"></i>

                            </div>


                            <h1>
                                Unable to Load Paper
                            </h1>


                            <p className="result-subtitle">

                                {error}

                            </p>


                            <div className="result-actions">

                                <button
                                    type="button"
                                    className="exam-btn secondary-btn"
                                    onClick={() =>
                                        navigate(
                                            "/free-mcq"
                                        )
                                    }
                                >

                                    <i className="bi bi-arrow-left me-2"></i>

                                    Back to Papers

                                </button>

                            </div>

                        </div>

                    </div>

                </main>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | No Questions
    |--------------------------------------------------------------------------
    */

    if (
        !questions.length
    ) {

        return (

            <div className="user-dashboard">

                <UserSidebar
                    sidebarOpen={
                        sidebarOpen
                    }
                    setSidebarOpen={
                        setSidebarOpen
                    }
                />


                <main className="dashboard-main">

                    <UserHeader
                        name={name}
                        setSidebarOpen={
                            setSidebarOpen
                        }
                    />


                    <div className="free-exam-page">

                        <div className="result-card">

                            <div className="result-icon">

                                <i className="bi bi-journal-x"></i>

                            </div>


                            <h1>
                                No Questions Found
                            </h1>


                            <p className="result-subtitle">

                                This paper does not contain any questions.

                            </p>


                            <div className="result-actions">

                                <button
                                    type="button"
                                    className="exam-btn secondary-btn"
                                    onClick={() =>
                                        navigate(
                                            "/free-mcq"
                                        )
                                    }
                                >

                                    <i className="bi bi-arrow-left me-2"></i>

                                    Back to Papers

                                </button>

                            </div>

                        </div>

                    </div>

                </main>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | MAIN EXAM
    |--------------------------------------------------------------------------
    */

    return (

        <div className="user-dashboard">


            {/* SIDEBAR */}

            <UserSidebar
                sidebarOpen={
                    sidebarOpen
                }
                setSidebarOpen={
                    setSidebarOpen
                }
            />


            <main className="dashboard-main">


                {/* HEADER */}

                <UserHeader
                    name={name}
                    setSidebarOpen={
                        setSidebarOpen
                    }
                />


                <div className="free-exam-page">


                    {/* TOP HEADER */}

                    <div className="exam-top-card">

                        <div>

                            <div className="exam-badge">

                                <i className="bi bi-journal-check me-2"></i>

                                FREE MCQ PRACTICE

                            </div>


                            <h1>

                                {paper?.paper ||
                                    "Medical MCQ Practice"}

                            </h1>


                            <p>

                                Paper ID:{" "}

                                {paperId ||
                                    "Demo Paper"}

                            </p>

                        </div>


                        {/* QUESTION + TIMER */}

                        <div className="exam-progress-wrapper">


                            <div className="exam-progress-box">

                                <span>
                                    Question
                                </span>


                                <strong>

                                    {currentQuestion + 1}

                                    <small>

                                        {" / "}

                                        {questions.length}

                                    </small>

                                </strong>

                            </div>


                            <div className="exam-progress-box exam-timer-box">

                                <span>
                                    Remaining Time
                                </span>


                                <strong>
                                    {formattedTime}
                                </strong>

                            </div>

                        </div>

                    </div>


                    {/* STATS */}

                    <div className="exam-stats">


                        <div className="exam-stat attempted">

                            <div className="stat-icon">

                                <i className="bi bi-check-lg"></i>

                            </div>


                            <div>

                                <span>
                                    Attempted
                                </span>

                                <strong>
                                    {attemptedCount}
                                </strong>

                            </div>

                        </div>


                        <div className="exam-stat unattempted">

                            <div className="stat-icon">

                                <i className="bi bi-circle"></i>

                            </div>


                            <div>

                                <span>
                                    Unattempted
                                </span>

                                <strong>
                                    {unattemptedCount}
                                </strong>

                            </div>

                        </div>


                        <div className="exam-stat tagged">

                            <div className="stat-icon">

                                <i className="bi bi-bookmark-fill"></i>

                            </div>


                            <div>

                                <span>
                                    Tagged
                                </span>

                                <strong>
                                    {taggedCount}
                                </strong>

                            </div>

                        </div>


                        <div className="exam-stat total">

                            <div className="stat-icon">

                                <i className="bi bi-list-ol"></i>

                            </div>


                            <div>

                                <span>
                                    Total
                                </span>

                                <strong>
                                    {questions.length}
                                </strong>

                            </div>

                        </div>

                    </div>


                    {/* MAIN GRID */}

                    <div className="exam-grid">


                        {/* QUESTION */}

                        <section className="question-card">


                            <div className="question-header">


                                <div className="question-number">

                                    Q.{currentQuestion + 1}

                                </div>


                                <button
                                    type="button"
                                    className={
                                        taggedQuestions.includes(
                                            question.id
                                        )
                                            ? "tag-btn active"
                                            : "tag-btn"
                                    }
                                    onClick={
                                        toggleTag
                                    }
                                >

                                    <i
                                        className={
                                            taggedQuestions.includes(
                                                question.id
                                            )
                                                ? "bi bi-bookmark-fill"
                                                : "bi bi-bookmark"
                                        }
                                    ></i>


                                    <span>

                                        {taggedQuestions.includes(
                                            question.id
                                        )
                                            ? "Tagged"
                                            : "Tag Question"}

                                    </span>

                                </button>

                            </div>


                            {/* QUESTION */}

                            <div className="question-text">

                                <h2
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            question.question ||
                                            ""
                                    }}
                                ></h2>

                            </div>


                            {/* OPTIONS */}

                            <div className="options-list">


                                {[
                                    {
                                        key: "A",
                                        text:
                                            question.option1
                                    },

                                    {
                                        key: "B",
                                        text:
                                            question.option2
                                    },

                                    {
                                        key: "C",
                                        text:
                                            question.option3
                                    },

                                    {
                                        key: "D",
                                        text:
                                            question.option4
                                    }

                                ].map(
                                    (
                                        option
                                    ) => {

                                        const selected =
                                            answers[
                                                question.id
                                            ] ===
                                            option.key;


                                        return (

                                            <button
                                                type="button"
                                                key={
                                                    option.key
                                                }
                                                className={
                                                    selected
                                                        ? "option-item selected"
                                                        : "option-item"
                                                }
                                                onClick={() =>
                                                    handleAnswer(
                                                        option.key
                                                    )
                                                }
                                            >

                                                <span className="option-letter">

                                                    {option.key}

                                                </span>


                                                <span
                                                    className="option-text"
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            option.text ||
                                                            ""
                                                    }}
                                                ></span>


                                                <span className="option-check">

                                                    {selected && (

                                                        <i className="bi bi-check-circle-fill"></i>

                                                    )}

                                                </span>

                                            </button>

                                        );

                                    }
                                )}

                            </div>


                            {/* NAVIGATION */}

                            <div className="question-navigation">


                                <button
                                    type="button"
                                    className="exam-btn secondary-btn"
                                    onClick={
                                        handlePrevious
                                    }
                                    disabled={
                                        currentQuestion ===
                                        0
                                    }
                                >

                                    <i className="bi bi-arrow-left me-2"></i>

                                    Previous

                                </button>


                                {currentQuestion ===
                                questions.length - 1 ? (

                                    <button
                                        type="button"
                                        className="exam-btn submit-btn"
                                        onClick={() =>
                                            handleSubmit(
                                                false
                                            )
                                        }
                                        disabled={
                                            submitting
                                        }
                                    >

                                        {submitting
                                            ? "Submitting..."
                                            : "Submit Paper"}

                                        <i className="bi bi-check2-circle ms-2"></i>

                                    </button>

                                ) : (

                                    <button
                                        type="button"
                                        className="exam-btn primary-btn"
                                        onClick={
                                            handleNext
                                        }
                                    >

                                        Next

                                        <i className="bi bi-arrow-right ms-2"></i>

                                    </button>

                                )}

                            </div>


                        </section>


                        {/* MOBILE NAVIGATOR BUTTON */}

                        <button
                            type="button"
                            className="mobile-navigator-btn"
                            onClick={() =>
                                setNavigatorOpen(
                                    true
                                )
                            }
                            aria-label="Open Question Navigator"
                        >

                            <i className="bi bi-grid-3x3-gap-fill"></i>

                            <span>
                                Questions
                            </span>

                        </button>


                        {/* MOBILE OVERLAY */}

                        {navigatorOpen && (

                            <div
                                className="navigator-overlay"
                                onClick={() =>
                                    setNavigatorOpen(
                                        false
                                    )
                                }
                            ></div>

                        )}


                        {/* QUESTION PALETTE */}

                        <aside
                            className={
                                navigatorOpen
                                    ? "question-palette mobile-open"
                                    : "question-palette"
                            }
                        >


                            {/* PALETTE HEADER */}

                            <div className="palette-header">

                                <div>

                                    <h3>
                                        Question Navigator
                                    </h3>

                                    <p>
                                        Jump to any question
                                    </p>

                                </div>


                                <div className="palette-header-actions">

                                    <i className="bi bi-grid-3x3-gap-fill"></i>


                                    <button
                                        type="button"
                                        className="mobile-navigator-close"
                                        onClick={() =>
                                            setNavigatorOpen(
                                                false
                                            )
                                        }
                                        aria-label="Close Question Navigator"
                                    >

                                        <i className="bi bi-x-lg"></i>

                                    </button>

                                </div>

                            </div>


                            {/* LEGEND */}

                            <div className="palette-legend">

                                <div>

                                    <span className="legend-dot current"></span>

                                    Current

                                </div>


                                <div>

                                    <span className="legend-dot attempted"></span>

                                    Attempted

                                </div>


                                <div>

                                    <span className="legend-dot tagged"></span>

                                    Tagged

                                </div>


                                <div>

                                    <span className="legend-dot unattempted"></span>

                                    Not Attempted

                                </div>

                            </div>


                            {/* QUESTION NUMBERS */}

                            <div className="question-numbers">

                                {questions.map(
                                    (
                                        item,
                                        index
                                    ) => {

                                        const isCurrent =
                                            currentQuestion ===
                                            index;


                                        const isAttempted =
                                            Boolean(
                                                answers[
                                                    item.id
                                                ]
                                            );


                                        const isTagged =
                                            taggedQuestions.includes(
                                                item.id
                                            );


                                        let className =
                                            "question-number-btn";


                                        if (
                                            isCurrent
                                        ) {

                                            className +=
                                                " current";

                                        } else if (
                                            isTagged
                                        ) {

                                            className +=
                                                " tagged";

                                        } else if (
                                            isAttempted
                                        ) {

                                            className +=
                                                " attempted";

                                        }


                                        return (

                                            <button
                                                type="button"
                                                key={
                                                    item.id
                                                }
                                                className={
                                                    className
                                                }
                                                onClick={() =>
                                                    goToQuestion(
                                                        index
                                                    )
                                                }
                                                title={
                                                    `Question ${
                                                        index + 1
                                                    }`
                                                }
                                            >

                                                {index + 1}


                                                {isTagged && (

                                                    <span className="mini-tag">

                                                        <i className="bi bi-bookmark-fill"></i>

                                                    </span>

                                                )}

                                            </button>

                                        );

                                    }
                                )}

                            </div>


                            {/* SUMMARY */}

                            <div className="palette-summary">

                                <div>

                                    <span>
                                        Attempted
                                    </span>

                                    <strong>
                                        {attemptedCount}
                                    </strong>

                                </div>


                                <div>

                                    <span>
                                        Remaining
                                    </span>

                                    <strong>
                                        {unattemptedCount}
                                    </strong>

                                </div>

                            </div>


                            {/* SUBMIT */}

                            <button
                                type="button"
                                className="palette-submit-btn"
                                onClick={() =>
                                    handleSubmit(
                                        false
                                    )
                                }
                                disabled={
                                    submitting
                                }
                            >

                                <i className="bi bi-send-check-fill me-2"></i>

                                {submitting
                                    ? "Submitting..."
                                    : "Submit Paper"}

                            </button>


                        </aside>


                    </div>


                </div>


            </main>


        </div>

    );

}


export default FreeMCQExam;